import { logIfDev } from 'utils/log';
import { fetchContentfulGql } from 'services/contentful';
import {
  GET_ALL_REVIEWS_COUNT,
  GET_MULTIPLE_CLINICS_REVIEWS,
} from 'data/graphql/queries/reviews';
import type { Clinic } from 'types/Clinic';
import type { ReviewItem } from 'types/Review';

export const MAX_FRONTEND_REVIEWS = 30;

export const getClinicsRatings = async (
  locale: string,
  slugs?: string[],
): Promise<ReviewItem[] | null> => {
  if (!slugs?.length) {
    return null;
  }

  const response = await fetchContentfulGql({
    query: GET_MULTIPLE_CLINICS_REVIEWS,
    variables: {
      locale,
      slugs,
    },
  });

  const { errors, data } = response;

  if (errors && !data) {
    logIfDev(errors);
    return null;
  }

  let { items } = data.reviews;
  // eslint-disable-next-line
  // @ts-ignore
  items = items.map((item) => {
    const { items: reviews } = item.linkedFrom.reviewCollection;
    const avg_rating = getAverageRating(reviews);

    return {
      ...item,
      avg_rating: !isNaN(avg_rating) ? avg_rating : null,
      total: reviews.length,
    };
  });

  return items;
};

export const getReviews = async (
  query: string,
  locale: string,
  slug?: string,
): Promise<ReviewItem[] | null> => {
  const response = await fetchContentfulGql({
    query,
    variables: {
      locale,
      slug,
    },
  });

  const { errors, data } = response;

  if (errors && !data) {
    logIfDev(errors);
    return null;
  }

  const { items } = data.reviews;

  let reviews = items;
  if (items[0]?.linkedFrom) {
    reviews = items[0]?.linkedFrom.reviewCollection.items;
  }

  const sortedReviews = reviews
    .filter(Boolean)
    .filter((review: ReviewItem) => {
      return !!review.date;
    })
    // eslint-disable-next-line
    // @ts-ignore
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  return sortedReviews;
};

export const getAllReviewsForCount = async (
  skip = 0,
): Promise<ReviewItem[] | null> => {
  const every = 1000;
  const response = await fetchContentfulGql({
    query: GET_ALL_REVIEWS_COUNT,
    variables: {
      skip,
    },
  });

  const { errors, data } = response;

  if (errors && !data) {
    logIfDev(errors);
    return null;
  }

  const { items, total } = response.data.reviews;

  if (total > every + skip) {
    const recursiveItems = await getAllReviewsForCount(skip + every);
    return [...items, ...(recursiveItems as ReviewItem[])];
  }

  return items;
};

export const getAverageRating = (
  reviews: ReviewItem[] | undefined | null,
): number => {
  if (!reviews) {
    return 0;
  }

  const sumOfOveralRatings = reviews?.reduce((acc, val) => {
    return acc + (val?.overallRating ?? 0);
  }, 0);

  const averageRating = sumOfOveralRatings / reviews?.length;
  return averageRating;
};

/**
 * Get correct reviews for clinic
 *
 * @param {Clinic[]|null} clinics
 * @param {Record<string, unknown>|null} reviews
 *
 * @returns {(Clinic[]|null)}
 */
export const findReviewForClinic = (
  clinics: Clinic[] | null,
  reviews: Record<string, unknown> | null,
): Clinic[] | null => {
  if (clinics !== null && reviews !== null) {
    const reviewsArray = Object.values(reviews) as Record<string, unknown>[];
    const clinicsWithReviews = clinics.map((clinic: Clinic) => ({
      ...clinic,
      reviews: reviewsArray.filter(
        (review: Record<string, unknown>) => review.slug === clinic.slug,
      ),
    }));

    return clinicsWithReviews;
  }

  return clinics;
};
